<template>
  <div
    class="category-card"
    :style="{ marginBottom: data.subcategoriesBlockHeight }"
    :class="{ active: isActive }"
    :id="category.id"
    :aria-label="'Category ' + translatedTitle + ' press space to select'"
    :aria-expanded="data.expand"
  >
    <!-- Desktop Collapse -->
    <el-collapse
      class="category-card__thumbnail--full"
      v-model="data.expand"
      @change="onCollapseChange"
    >
      <el-collapse-item :name="category.id" role="button">
        <template #title>
          <div
            class="category-card__thumbnail--full"
            :style="{ fontFamily: helperStore.headerFont }"
          >
            <i
              :class="category.icon || 'icon-House'"
              :style="`color: ${helperStore.styleData.accentColor}`"
              class="category-card__ic"
            ></i>

            <div class="category-card__name">{{ truncatedTitle }}</div>
          </div>
        </template>

        <div class="category-card__subcategories--full">
          <div
            v-for="(subcategory, index) in category.subcategories"
            @click="selectSubcategory(index)"
            :key="subcategory.id"
            :style="{
              color:
                activeSubcategoryId === subcategory.id
                  ? helperStore.styleData.accentColor
                  : 'inherit'
            }"
            :aria-label="'Subcategory ' + getSubcategoryTitle(subcategory)"
            :aria-current="activeSubcategoryId === subcategory.id"
            class="category-card__subcategory full"
          >
            <span>{{ getSubcategoryTitle(subcategory) }}</span>
            <i class="icon-Arrow-Right"></i>
            <a
              :href="`#${subcategory?.topics[0]?.id}`"
              :aria-label="`Skip to topic content ${getSubcategoryTitle(subcategory)}`"
              class="accessibility-link"
            ></a>
          </div>
        </div>
      </el-collapse-item>
    </el-collapse>

    <!-- Mobile Thumbnail -->
    <div class="category-card__thumbnail" @click="onCollapseChange">
      <i
        :class="category.icon || 'icon-House'"
        :style="`color: ${helperStore.styleData.accentColor}`"
        class="category-card__ic"
      ></i>

      <div class="category-card__name">
        {{ truncatedTitle }}
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { isMobile } from '@/utils/helpers'
import { Category, Subcategory } from '@/interfaces/guide.interface'
import { computed, onMounted, reactive, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useHelperStore } from '@/pinia/helper.store'

const helperStore = useHelperStore()
const route = useRoute()
const router = useRouter()

const emit = defineEmits<{
  (e: 'collapse-change', category: null | Category): void
}>()
const props = defineProps<{ category: Category; activeCategory: Category | null }>()
const data = reactive({
  expand: null,
  subcategoriesBlockHeight: '0px'
})

const isActive = computed(
  () => props.activeCategory && props.category.id === props.activeCategory.id
)
const activeSubcategoryId = computed(() => {
  const activeSubcategory = props.category.subcategories.find(
    (s) => s.id === +route.params.subcategoryId
  )

  return activeSubcategory && activeSubcategory.id
})
const translatedTitle = computed(
  () => props.category.title_translations?.[helperStore.currentLanguage]
)
const truncatedTitle = computed(() => {
  const title = translatedTitle.value
  const TITLE_LENGTH_LIMIT = isMobile() ? 35 : 50

  return title?.length > TITLE_LENGTH_LIMIT ? title.slice(0, TITLE_LENGTH_LIMIT) + '...' : title
})

watch(isActive, (newValue) => {
  if (newValue) {
    data.expand = props.activeCategory.id
    const subs = document.getElementById(props.category.subcategories[0].id.toString())

    if (subs) {
      data.subcategoriesBlockHeight = `${subs.clientHeight}px`
    }

    return
  }

  data.expand = null
  data.subcategoriesBlockHeight = `0px`
})

onMounted(() => {
  if (isActive.value) {
    data.expand = props.category.id
  }
})

function getSubcategoryTitle(subcategory: Subcategory): string {
  return subcategory.title_translations?.[helperStore.currentLanguage]
}

function onCollapseChange() {
  if (isActive.value) {
    data.expand = null
    // collapsed or closed
    emit('collapse-change', null)
  } else {
    // expanded or opened
    emit('collapse-change', props.category)
    data.expand = props.category.id
  }
}
function selectSubcategory(subcategoryIndex) {
  const selectedSubcategory = props.category.subcategories[subcategoryIndex]

  router.push({ params: { subcategoryId: selectedSubcategory.id } })
}
</script>
